import { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Mail,
  Copy,
  Check,
  X,
  Loader2,
  Forward,
} from 'lucide-react';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useApi from '../../api';
import AgentTabHeader from './AgentTabHeader';
import { EmailChannelTabProps } from './types';

// Email channel settings are NON-versioned runtime config (unlike most
// builder tabs): saves apply immediately via PUT, with no draft/publish
// step — the inbound address is a live global routing key that must never
// rewind on a version restore. So this tab never calls setNeedsUpdate.

interface EmailChannelState {
  enabled: boolean;
  address_local_part: string | null;
  from_name: string | null;
  inbound_address: string | null;
  channel_configured: boolean;
}

const LOCAL_PART_RE = /^[a-z0-9][a-z0-9._-]{1,62}$/;

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy', toastConfig);
    }
  }, [text]);
  return (
    <Button variant="outline" size="sm" onClick={onCopy} className="h-7 shrink-0 gap-1.5 text-xs">
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

export default function EmailChannelTab({ agId, agentName }: EmailChannelTabProps) {
  const { get, put } = useApi();
  const [state, setState] = useState<EmailChannelState | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [localPart, setLocalPart] = useState('');
  const [fromName, setFromName] = useState('');
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState<'unknown' | 'checking' | 'available' | 'taken'>(
    'unknown',
  );
  // The local part persisted on the server — its own value is always "available".
  const savedLocalPartRef = useRef<string | null>(null);

  useEffect(() => {
    if (!agId) return;
    let active = true;
    get(`/agents/${agId}/email-channel`)
      .then(({ data }) => {
        if (!active) return;
        setState(data);
        setEnabled(!!data.enabled);
        savedLocalPartRef.current = data.address_local_part;
        setLocalPart(data.address_local_part || slugify(agentName || ''));
        setFromName(data.from_name || '');
      })
      .catch(() => {
        toast.error('Failed to load email channel settings', toastConfig);
      });
    return () => {
      active = false;
    };
  }, [agId, get, agentName]);

  // Debounced availability check while the address is edited.
  useEffect(() => {
    if (!agId) return;
    const candidate = localPart.trim().toLowerCase();
    if (!candidate || candidate === savedLocalPartRef.current) {
      setAvailability('unknown');
      return;
    }
    if (!LOCAL_PART_RE.test(candidate)) {
      setAvailability('unknown');
      return;
    }
    setAvailability('checking');
    const t = setTimeout(() => {
      get(`/agents/${agId}/email-channel/availability?local_part=${encodeURIComponent(candidate)}`)
        .then(({ data }) => setAvailability(data.available ? 'available' : 'taken'))
        .catch(() => setAvailability('unknown'));
    }, 500);
    return () => clearTimeout(t);
  }, [agId, localPart, get]);

  const save = useCallback(
    async (next?: Partial<{ enabled: boolean }>) => {
      if (!agId) return;
      const candidate = localPart.trim().toLowerCase();
      if (!LOCAL_PART_RE.test(candidate)) {
        toast.error(
          'Address must be 2-63 lowercase letters, numbers, dots, hyphens or underscores.',
          toastConfig,
        );
        return;
      }
      setSaving(true);
      try {
        const { data } = await put(`/agents/${agId}/email-channel`, {
          enabled: next?.enabled ?? enabled,
          address_local_part: candidate,
          from_name: fromName.trim() || null,
        });
        setState(data);
        setEnabled(!!data.enabled);
        savedLocalPartRef.current = data.address_local_part;
        setAvailability('unknown');
        toast.success('Email channel saved', toastConfig);
      } catch (err: any) {
        toast.error(err?.detail || 'Failed to save email channel', toastConfig);
      } finally {
        setSaving(false);
      }
    },
    [agId, enabled, localPart, fromName, put],
  );

  const handleEnableChange = (value: boolean) => {
    setEnabled(value);
    // Persist immediately — this tab is outside the draft/publish flow.
    save({ enabled: value });
  };

  if (state && !state.channel_configured) {
    return (
      <div className="min-w-0 max-w-full">
        <AgentTabHeader
          icon={Mail}
          title="Email"
          description="Let people email your agent and get AI replies in their inbox."
        />
        <div className="mx-auto w-full max-w-4xl py-6">
          <div className="rounded-lg border bg-card p-6 text-center">
            <p className="text-sm font-medium">Email channel is not configured on this platform</p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
              The sending infrastructure hasn&apos;t been set up yet. Contact support to enable the
              email channel.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const inboundDomain = state?.inbound_address?.split('@')[1];
  const previewAddress =
    state?.inbound_address ||
    (inboundDomain ? `${localPart}@${inboundDomain}` : null);

  return (
    <div className="min-w-0 max-w-full">
      <AgentTabHeader
        icon={Mail}
        title="Email"
        description="Let people email your agent and get AI replies in their inbox."
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="email-toggle" className="cursor-pointer text-xs text-muted-foreground">
              {enabled ? 'Enabled' : 'Disabled'}
            </Label>
            <Switch id="email-toggle" checked={enabled} onCheckedChange={handleEnableChange} />
          </div>
        }
      />

      <div className="mx-auto min-w-0 w-full max-w-4xl space-y-8 py-6">
        {!enabled && (
          <div className="rounded-lg border bg-card p-6 text-center">
            <p className="text-sm font-medium">The email channel is off</p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
              Turn it on to give this agent an email address. Inbound emails become conversations
              in your dashboard, the AI replies by email, and human takeover works the same as
              chat. Conversations appear marked &quot;Email&quot;.
            </p>
          </div>
        )}

        {/* Address */}
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Agent address</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              The address this agent receives at and sends from. Replies thread automatically.
            </p>
          </div>
          <div className="rounded-lg border bg-card p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Input
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                  placeholder="your-company"
                  className="w-56 pr-8 font-mono text-xs"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  {availability === 'checking' && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  )}
                  {availability === 'available' && (
                    <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                  )}
                  {availability === 'taken' && (
                    <X className="h-3.5 w-3.5 text-destructive" />
                  )}
                </span>
              </div>
              {inboundDomain && (
                <span className="font-mono text-xs text-muted-foreground">@{inboundDomain}</span>
              )}
            </div>
            {availability === 'taken' && (
              <p className="text-[11px] text-destructive">
                This address is taken by another agent — pick a different one.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email-from-name" className="text-xs">
                From name
              </Label>
              <Input
                id="email-from-name"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder={agentName || 'Agent name'}
                className="w-full max-w-72"
              />
              <p className="text-[11px] text-muted-foreground">
                Shown as the sender in the recipient&apos;s inbox. Defaults to the agent&apos;s
                name.
              </p>
            </div>

            <div className="flex justify-end">
              <Button size="sm" onClick={() => save()} disabled={saving || availability === 'taken'}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </div>
        </div>

        {enabled && previewAddress && (
          <>
            {/* Live address */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Your agent&apos;s email address</h3>
              <div className="flex min-w-0 items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
                <p className="truncate font-mono text-xs">{previewAddress}</p>
                <CopyButton text={previewAddress} />
              </div>
            </div>

            {/* Forwarding */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Forward className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Use your own address</h3>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <p className="text-xs leading-5 text-muted-foreground">
                  Want people to write to <span className="font-medium">support@yourcompany.com</span>?
                  Set up auto-forwarding from that mailbox to{' '}
                  <span className="font-mono text-[11px]">{previewAddress}</span> in your email
                  provider (Google Workspace: Settings → Forwarding; Microsoft 365: mail flow
                  rule). The agent replies from its own address with your From name; replies
                  continue the same conversation.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
